/**
 * Which OTIF Analysis tab a click on the OTIF chart opens. Shared by the
 * Default tab and the Site KPI delivery panel so the two charts cannot drift.
 *
 * CR-7270 rework (Sarah's retest, 16 Oct 2026): a click on the chart reports
 * Recharts' `activePayload`, which lists EVERY series at the clicked month —
 * not the segment under the pointer. Reading the first known series from it
 * opened the same tab whichever coloured part was clicked, so the yellow
 * Arrears bar opened "Failed OTIF Lines" and read "No records found" over a
 * month full of arrears. A segment now names its own tab through its
 * `<Bar onClick>` (the Live NCRs Overdue pattern, `liveNcrsDrill.ts`).
 */

/** An OTIF Analysis tab a chart segment opens. */
export type OtifSegmentTab = 'failed' | 'arrears' | 'positive'

/** The chart's bar series (`dataKey`) and the tab that lists its lines. */
const SEGMENT_TABS: Record<string, OtifSegmentTab> = {
  failed: 'failed',
  arrears: 'arrears',
  otifCount: 'positive',
}

/**
 * The tab a click on the OTIF chart opens.
 *
 * A click on a coloured segment names it (`segment`) and opens that segment's
 * tab. A click elsewhere in the month cannot tell which part was meant, so it
 * keeps the chart's older rule: the first bar series the click reports, or
 * none (the modal's own default).
 *
 * @param activePayload - the series Recharts reports for the clicked month
 * @param segment - the segment clicked, when the click landed on one
 * @returns the tab to open, or undefined for the modal's default
 * @example otifInitialTab([{ dataKey: 'otifCount' }, { dataKey: 'arrears' }], 'arrears') // 'arrears'
 */
export function otifInitialTab(
  activePayload: ReadonlyArray<{ dataKey?: unknown }> | undefined,
  segment?: OtifSegmentTab,
): OtifSegmentTab | undefined {
  if (segment) return segment
  for (const item of activePayload ?? []) {
    const tab = SEGMENT_TABS[String(item.dataKey)]
    if (tab) return tab
  }
  return undefined
}

/** The part of a Recharts bar click this chart reads: the clicked point. */
interface OtifBarClick {
  payload?: { month?: unknown }
}

/**
 * A `<Bar onClick>` handler for one segment of the OTIF chart.
 *
 * Stops the click reaching the chart's own `onClick`, which would open the
 * modal a second time without the segment.
 *
 * @param segment - the tab this bar's lines are listed on
 * @param open - opens the modal for a month and segment
 * @returns the handler Recharts calls with the clicked bar
 */
export function otifBarClick(
  segment: OtifSegmentTab,
  open: (month: string, segment: OtifSegmentTab) => void,
): (bar: OtifBarClick | undefined, index: number, event?: { stopPropagation?: () => void }) => void {
  return (bar, _index, event) => {
    event?.stopPropagation?.()
    const month = bar?.payload?.month
    if (typeof month === 'string' && month) open(month, segment)
  }
}
